import { useEffect, useMemo, useRef, useState } from "react";
import { GoUpload } from "react-icons/go";
import { useSearchParams } from "react-router-dom";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import OvertimeModalForm from "../modals/OvertimeModalForm";
import { AiOutlinePlus } from "react-icons/ai";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import {
	useDeleteOvertimeGroup,
	useGetOvertimeGroup,
	useGetOvertimeGroupSearch,
} from "../../../../services/overtime-group.servicet";
import {
	useDeleteOvertime,
	useGetOvertime,
	useGetOvertimeSearch,
} from "../../../../services/overtime.service";
import { OvertimeDto } from "../../../../models/overtime.dto";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import TableFilter from "../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../components/search/APISearchInput";
import { useGetCompanyCurrencyConfig } from "../../../../services/company-currency.service";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import UploadOvertime from "../modals/UploadOvertime";
import { toast } from "react-toastify";
import { getAccessToken } from "../../../../services/utils/app.data";
import ENDPOINTS from "../../../../services/utils/endpoints";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import {
	overtimeGroupColumns,
	overtimeGroupColumnVisibility,
	overtimeColumns,
	overtimeColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import OvertimeGroupModalForm from "../modals/OvertimeGroupModalForm";
import ActionPopover from "../../../../components/popovers/actionPopover";
import ExportOvertimeModal from "../modals/ExportOvertimeModal";
// import ExportOvertimeModal from "../modals/ExportOvertimeModal";

const OvertimeView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<"overtimeGroups" | "overtimes">(
		"overtimeGroups",
	);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openOvertimeGroupModal, setOpenOvertimeGroupModal] = useState(false);
	const [openOvertimeModal, setOpenOvertimeModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openUploadOvertimeModal, setOpenUploadOvertimeModal] = useState(false);
	const [openExportOvertimeModal, setOpenExportOvertimeModal] = useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Overtime Groups
	const {
		columnVisibilityModel: overtimeGroupColumnVisibilityModel,
		setColumnVisibilityModel: setOvertimeGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(overtimeGroupColumnVisibility);

	// Column visibility for Overtimes
	const {
		columnVisibilityModel: overtimeColumnVisibilityModel,
		setColumnVisibilityModel: setOvertimeColumnVisibilityModel,
	} = useColumnVisibilityModel(overtimeColumnVisibility);

	// Overtime Groups table
	const { mutate: searchOvertimeGroups } = useGetOvertimeGroupSearch(currentCompany?.id as number);
	const {
		tableAPIRef: overtimeGroupTableAPIRef,
		fetchedTableData: fetchedOvertimeGroupData,
		tableData: overtimeGroupTableData,
		isLoading: overtimeGroupLoading,
		pageCount: overtimeGroupPageCount,
		setTableData: setOvertimeGroupTableData,
	} = useManageTableWithSearchParams<OvertimeDto>({
		fetchHook: (options) =>
			useGetOvertimeGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Overtimes table
	const { mutate: searchOvertimes } = useGetOvertimeSearch();
	const {
		tableAPIRef: overtimeTableAPIRef,
		fetchedTableData: fetchedOvertimeData,
		tableData: overtimeTableData,
		isLoading: overtimeLoading,
		pageCount: overtimePageCount,
		setTableData: setOvertimeTableData,
	} = useManageTableWithSearchParams<OvertimeDto>({
		fetchHook: useGetOvertime,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: overtimeGroups } = useGetOvertimeGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: EmployeeBandOrderBy.CREATED_AT_DESC,
	});
	const { data: companyCurrencies } = useGetCompanyCurrencyConfig({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
	});

	// The Employee Overtimes list endpoint only returns `groupId` and
	// `employeeBandId` as flat foreign keys — the expanded `group: { name }`
	// and `employeeBand: { name }` relations that the table columns read are
	// never included. Join them in on the client using the datasets already
	// fetched for the form dropdowns so the columns populate correctly
	// regardless of whether the row came from the initial load or a search.
	const enrichedOvertimeTableData = useMemo(() => {
		if (!overtimeTableData?.length) return overtimeTableData;

		return overtimeTableData.map((row: any) => {
			const group = row?.group?.name
				? row.group
				: overtimeGroups?.data?.find((g: any) => g.id === row?.groupId);

			const employeeBand = row?.employeeBand?.name
				? row.employeeBand
				: employeeBands?.data?.find((b: any) => b.id === row?.employeeBandId);

			return { ...row, group, employeeBand };
		});
	}, [overtimeTableData, overtimeGroups, employeeBands]);

	const handleClose = () => setAnchorEl(null);

	const handleOpenOvertimeGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenOvertimeGroupModal(true);
	};

	const handleOpenOvertimeModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenOvertimeModal(true);
	};

	const handleTabChange = (tab: "overtimeGroups" | "overtimes") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteOvertimeGroup } = useDeleteOvertimeGroup();
	const { mutate: deleteOvertime } = useDeleteOvertime();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "overtimeGroups") {
			deleteOvertimeGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteOvertime(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	const handleOvertimeExport = async () => {
		try {
			if (!currentCompany?.id) {
				toast.error("Company not selected.");
				return;
			}

			const exportUrl =
				ENDPOINTS.PROCESSING_COMPANY.EXPORTS.OVERTIME_EXPORT.replace(
					"{companyId}",
					currentCompany.id.toString(),
				);

			const response = await fetch(exportUrl, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${getAccessToken()}`,
				},
			});

			if (!response.ok) {
				toast.error("Overtime export failed. Please try again.");
				return;
			}

			const contentDisposition = response.headers.get("Content-Disposition");
			const filename =
				contentDisposition?.split("filename=")[1] || "overtime_export.xlsx";

			const blob = await response.blob();

			const link = document.createElement("a");
			link.href = window.URL.createObjectURL(blob);
			link.download = filename;
			link.click();
			window.URL.revokeObjectURL(link.href);

			toast.success("Overtime exported successfully.");
		} catch (error) {
			console.error(error);
			toast.error("Unable to download overtime.");
		}
	};

	return (
		<div className="font-inter">
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={
							activeTab === "overtimeGroups"
								? fetchedOvertimeGroupData
								: fetchedOvertimeData
						}
						setTableData={
							activeTab === "overtimeGroups"
								? setOvertimeGroupTableData
								: setOvertimeTableData
						}
						mutate={
							activeTab === "overtimeGroups"
								? searchOvertimeGroups
								: searchOvertimes
						}
					/>
					<TableFilter
						fields={
							activeTab === "overtimeGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{
													title: "Min Hours Required",
													fieldName: "minHoursRequired",
												},
												{
													title: "Max Hours Permitted",
													fieldName: "maxHoursPermitted",
												},
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Min Hours Required",
											fieldName: "minHoursRequired",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Max Hours Permitted",
											fieldName: "maxHoursPermitted",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Taxable",
											fieldName: "taxable",
											fieldType: "YES_NO",
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{
													title: "Min Hours Required",
													fieldName: "minHoursRequired",
												},
												{
													title: "Max Hours Permitted",
													fieldName: "maxHoursPermitted",
												},
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Employee Band",
											fieldName: "employeeBandId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Overtime Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Taxable",
											fieldName: "taxable",
											fieldType: "YES_NO",
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "overtimeGroups"
								? (overtimeGroupColumns as any)
								: (overtimeColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "overtimeGroups"
								? overtimeGroupColumnVisibility
								: overtimeColumnVisibility
						}
						visibilityModel={
							activeTab === "overtimeGroups"
								? overtimeGroupColumnVisibilityModel
								: overtimeColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "overtimeGroups"
								? setOvertimeGroupColumnVisibilityModel
								: setOvertimeColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "overtimeGroups"
								? overtimeGroupTableAPIRef
								: overtimeTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{activeTab === "overtimeGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadOvertimeModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Overtime Group
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Allowances tab */}
					{activeTab === "overtimes" && (
						<button
							onClick={() => setOpenExportOvertimeModal(true)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Overtime
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
					>
						{activeTab === "overtimeGroups" ? (
							<button
								onClick={handleOpenOvertimeGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Overtime Group
							</button>
						) : (
							<button
								onClick={handleOpenOvertimeModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Overtime
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("overtimeGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "overtimeGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Overtime Groups
				</button>
				<button
					onClick={() => handleTabChange("overtimes")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "overtimes"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Overtimes
				</button>
			</div>

			{activeTab === "overtimeGroups" ? (
				<TanStackEmployeeTable
					ref={overtimeGroupTableAPIRef}
					tableId={TableId.OVERTIME_GROUPS}
					data={overtimeGroupTableData}
					columns={overtimeGroupColumns}
					columnVisibility={overtimeGroupColumnVisibilityModel}
					onColumnVisibilityChange={setOvertimeGroupColumnVisibilityModel}
					loading={overtimeGroupLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={overtimeGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={overtimeGroupPageCount}
					apiRef={overtimeGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={overtimeTableAPIRef}
					tableId={TableId.OVERTIME}
					data={enrichedOvertimeTableData}
					columns={overtimeColumns}
					columnVisibility={overtimeColumnVisibilityModel}
					onColumnVisibilityChange={setOvertimeColumnVisibilityModel}
					loading={overtimeLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={overtimeColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={overtimePageCount}
					apiRef={overtimeTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "overtimeGroups"
						? setOpenOvertimeGroupModal
						: setOpenOvertimeModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>

			{openOvertimeGroupModal && (
				<OvertimeGroupModalForm
					open={openOvertimeGroupModal}
					setOpen={setOpenOvertimeGroupModal}
					title={
						isEditForm ? "Update Overtime Group" : "Add New Overtime Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={overtimeGroupTableAPIRef}
					reloadTableData={reloadTableData}
				/>
			)}
			{openOvertimeModal && (
				<OvertimeModalForm
					open={openOvertimeModal}
					setOpen={setOpenOvertimeModal}
					title={isEditForm ? "Update Overtime Type" : "Add New Overtime Type"}
					isEditForm={isEditForm}
					tableAPIRef={overtimeTableAPIRef}
					employeeBands={employeeBands?.data || []}
					companyCurrencies={companyCurrencies?.data || []}
					overtimeGroups={overtimeGroups?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadOvertimeModal && (
				<UploadOvertime
					open={openUploadOvertimeModal}
					setOpen={setOpenUploadOvertimeModal}
				/>
			)}
			{openExportOvertimeModal && (
				<ExportOvertimeModal
					open={openExportOvertimeModal}
					setOpen={setOpenExportOvertimeModal}
					companyId={currentCompany?.id as number}
				/>
			)}
		</div>
	);
};

export default OvertimeView;